import React, {
	PropsWithChildren,
	createContext,
	memo,
	useCallback,
	useContext,
	useMemo,
	useState,
} from "react";
import { LatLngLiteral } from "leaflet";
import { useInternal } from "uikit";

import { Language, Map as Search } from "../../../../../services";
import { useTypedSelector } from "../../../../../redux/store";

export const PointOrderModalTableContext =
	createContext<Provider.Context | null>(null);

export const usePointOrderModalTableContext = (): Provider.Context => {
	const store = useContext<Provider.Context | null>(
		PointOrderModalTableContext,
	);
	if (!store) {
		throw new Error(
			"Missing PointOrderModalTableContext.Provider in the tree",
		);
	}
	return store;
};

export const PointOrderModalTableProvider: React.FC<Provider.Props> = ({
	children,
}): JSX.Element => {
	const lang = useTypedSelector((state) => state.session.language);
	const [rowId, setRowId] = useInternal<Provider.Value["rowId"]>(-1);
	const [reload, setReload] = useInternal<Provider.Value["reload"]>(false);
	const [reload2, setReload2] = useInternal<Provider.Value["reload2"]>(0);
	const [disabled, setDisabled] =
		useInternal<Provider.Value["disabled"]>(false);
	const [customerId, setCustomerId] =
		useInternal<Provider.Value["customerId"]>(-1);
	// value

	const [settlements, setSettlements] = useState<
		Provider.Value["settlements"]
	>(new Map());
	const [streets, setStreets] = useState<Provider.Value["streets"]>(
		new Map(),
	);
	const [settlementsCoordinates, setSettlementsCoordinates] = useState<
		Provider.Value["mapCoordinates"]
	>(new Map());
	const [streetsCoordinates, setStreetsCoordinates] = useState<
		Provider.Value["mapCoordinates"]
	>(new Map());
	const [housesCoordinates, setHousesCoordinates] = useState<
		Provider.Value["mapCoordinates"]
	>(new Map());
	const [hadChangeStreets, setHadChangeStreets] = useState<
		Provider.Value["hadChangeStreets"]
	>(new Map());

	const [cityCellStates, setCityCellStates] = useState<
		Map<number, Provider.CellInputState>
	>(new Map());

	const [streetCellStates, setStreetCellStates] = useState<
		Map<number, Provider.CellInputState>
	>(new Map());

	const defaultObjectCellState = useMemo<Provider.CellInputState>(
		() => ({
			isManual: false,
			isSelected: false,
		}),
		[],
	);

	const getData = useCallback<Provider.Context["getData"]>(
		(id) => {
			if (id) {
				const settlement = settlements.get(id);
				const street = streets.get(id);
				const settlementCoordinates = settlementsCoordinates.get(id);
				const streetCoordinates = streetsCoordinates.get(id);
				const houseCoordinates = housesCoordinates.get(id);
				const hadChangeStreet = hadChangeStreets.get(id);

				const cityCellState =
					cityCellStates.get(id) || defaultObjectCellState;
				const streetCellState =
					streetCellStates.get(id) || defaultObjectCellState;

				setReload(false);
				setRowId(-1);

				return {
					street,
					settlement,
					streetCoordinates,
					settlementCoordinates,
					houseCoordinates,
					hadChangeStreet,
					//
					cityCellState,
					streetCellState,
				};
			}
			setRowId(-1);
			setReload(false);
			return {
				street: undefined,
				settlement: undefined,
				hadChangeStreet: undefined,
				//
				cityCellState: defaultObjectCellState,
				streetCellState: defaultObjectCellState,
			};
		},
		[
			cityCellStates,
			defaultObjectCellState,
			hadChangeStreets,
			housesCoordinates,
			setReload,
			setRowId,
			settlements,
			settlementsCoordinates,
			streetCellStates,
			streets,
			streetsCoordinates,
		],
	);

	const setCellState: Provider.SetCellState = useCallback(
		(
			id: number,
			options: {
				typeCell: "city" | "street";
				typeElement: "isManual" | "isSelected";
				reset?: boolean;
			},
		) => {
			const setters = {
				city: setCityCellStates,
				street: setStreetCellStates,
			};

			const setter = setters[options.typeCell];

			setter?.((prev) => {
				const next = new Map(prev);
				const current = next.get(id) || defaultObjectCellState;

				if (options.reset) {
					next.set(id, defaultObjectCellState);
					return next;
				}

				const updated: Provider.CellInputState =
					options.typeElement === "isManual"
						? { isManual: true, isSelected: false }
						: { isManual: false, isSelected: true };

				const newObject = { ...current, ...updated };

				// console.group("Conlose_log_DP2");

				// console.log("id", id);
				// console.log("options", options);
				// console.log("updated", updated);
				// console.groupEnd();

				next.set(id, newObject);
				return next;
			});

			setRowId(id);
			setReload(true);
			setReload2((pre) => pre + 1);
		},
		[defaultObjectCellState, setReload, setReload2, setRowId],
	);

	const reset = useCallback<Provider.Context["reset"]>(
		(id: number) => {
			streets.set(id, undefined);
			streetsCoordinates.set(id, undefined);
			housesCoordinates.set(id, undefined);
			setRowId(id);
			setReload(true);
		},
		[housesCoordinates, setReload, setRowId, streets, streetsCoordinates],
	);

	const addStreet = useCallback<Provider.Context["addStreet"]>(
		(streetObject: Search.Search.Object | undefined, id: number) => {
			setStreets((prev) => prev.set(id, streetObject));
			setStreetsCoordinates((prev) =>
				prev.set(id, streetObject?.coordinates),
			);
			setReload(true);
			setRowId(id);
		},
		[setReload, setRowId, setStreetsCoordinates],
	);

	const handleChangeStreet = useCallback<
		Provider.Context["handleChangeStreet"]
	>(
		(id) => {
			if (!id) return;

			setHadChangeStreets((prev) => {
				const next = new Map(prev);
				const prevValue = next.get(id) ?? 0;
				next.set(id, prevValue + 1);
				return next;
			});

			setReload(true);
			setRowId(id);
		},
		[setReload, setRowId],
	);

	const addSettlement = useCallback<Provider.Context["addSettlement"]>(
		(settlementObject, id) => {
			if (!settlementObject) {
				reset(id);
				return;
			}

			setSettlements((prev) => prev.set(id, settlementObject));
			setSettlementsCoordinates((prev) =>
				prev.set(id, settlementObject.coordinates),
			);
			setStreets((prev) => prev.set(id, undefined));
			setStreetsCoordinates((prev) => prev.set(id, undefined));
			setReload(true);
			setRowId(id);
		},
		[reset, setReload, setRowId, setStreetsCoordinates],
	);

	const addSettlementCoordinates = useCallback<
		Provider.Context["addSettlementCoordinates"]
	>(
		(coordinates, id) => {
			setSettlementsCoordinates((prev) => prev.set(id, coordinates));
			setRowId(id);
			setReload(true);
		},
		[setReload, setRowId],
	);

	const addStreetCoordinates = useCallback<
		Provider.Context["addStreetCoordinates"]
	>(
		(coordinates, id) => {
			setStreetsCoordinates((prev) => prev.set(id, coordinates));
			setRowId(id);
			setReload(true);
		},
		[setReload, setRowId],
	);

	const addHouseCoordinates = useCallback<
		Provider.Context["addHouseCoordinates"]
	>(
		(coordinates, id) => {
			setHousesCoordinates((prev) => prev.set(id, coordinates));
			setRowId(id);
			setReload(true);
		},
		[setReload, setRowId],
	);

	return (
		<PointOrderModalTableContext.Provider
			value={useMemo(
				() => ({
					reload,
					reload2,
					getData,

					addSettlement,
					addStreet,
					handleChangeStreet,
					addSettlementCoordinates,
					addStreetCoordinates,
					addHouseCoordinates,
					rowId,
					setRowId,

					disabled,
					setDisabled,

					customerId,
					setCustomerId,

					reset,
					lang,

					setCellState,
				}),
				[
					reload,
					reload2,
					getData,
					addSettlement,
					addStreet,
					handleChangeStreet,
					addSettlementCoordinates,
					addStreetCoordinates,
					addHouseCoordinates,
					rowId,
					setRowId,
					disabled,
					setDisabled,
					customerId,
					setCustomerId,
					reset,
					lang,

					setCellState,
				],
			)}
		>
			{children}
		</PointOrderModalTableContext.Provider>
	);
};

export declare namespace Provider {
	export interface Props extends PropsWithChildren {}

	type Settlement = Search.Search.Object;
	type Street = Search.Search.Object;
	interface GetData {
		settlement: Settlement | undefined;
		street: Street | undefined;
		streetCoordinates?: LatLngLiteral | undefined;
		settlementCoordinates?: LatLngLiteral | undefined;
		houseCoordinates?: LatLngLiteral | undefined;
		hadChangeStreet: number | undefined;
		cityCellState: CellInputState;
		streetCellState: CellInputState;
	}
	interface Value {
		disabled: boolean;
		customerId: number;
		rowId: number;
		reload: boolean;
		reload2: number;

		mapCoordinates: Map<number, LatLngLiteral | undefined>;
		settlements: Map<number, Settlement | undefined>;
		streets: Map<number, Street | undefined>;
		hadChangeStreets: Map<number, number | undefined>;
	}
	interface Context {
		lang: Language;
		reset: (id: number) => void;
		getData: (id: number) => GetData;

		reload: boolean;
		reload2: number;

		addSettlement: (settlement: Settlement | undefined, id: number) => void;
		addStreet: (street: Street | undefined, id: number) => void;

		handleChangeStreet: (id: number) => void;

		addSettlementCoordinates: (
			coordinates: LatLngLiteral | undefined,
			id: number,
		) => void;
		addStreetCoordinates: (
			coordinates: LatLngLiteral | undefined,
			id: number,
		) => void;

		addHouseCoordinates: (
			coordinates: LatLngLiteral | undefined,
			id: number,
		) => void;

		rowId: number;
		setRowId: (value: number) => void;

		disabled: boolean;
		setDisabled: (value: boolean) => void;

		customerId?: number;
		setCustomerId: (value: number) => void;

		setCellState: SetCellState;
	}

	type CellInputState = {
		isManual: boolean; //* The last character was manually entered in the cell
		isSelected: boolean; //* The last event was due to the user selecting in the selector in the cell
	};

	interface SetCellStateOptions {
		typeCell: "city" | "street";
		typeElement: "isManual" | "isSelected";
		reset?: boolean;
	}

	type SetCellState = (id: number, options: SetCellStateOptions) => void;
}

export const PointOrderModalTableProviderMemo = memo(
	PointOrderModalTableProvider,
);

export const ConstantAccessors = {
	id: "id",
	city: "city",
	house: "house",
	street: "street",
	entrance: "entrance",
	flat: "flat",
	polygon: "polygon",
	setting: "setting",
} as const;
export type TypeAccessors = typeof ConstantAccessors;
export type ValueAccessors =
	(typeof ConstantAccessors)[keyof typeof ConstantAccessors];
